import { useSuspenseQuery } from "@apollo/client";
import { Link } from "@tanstack/react-router";
import { BarChart3, Lock, TrendingUp, DollarSign, Activity, Heart, Package, AlertTriangle } from "lucide-react";
import React, { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { groupReportsByCategory } from "./utils";
import { ErrorCard } from "@/components/ErrorCard";
import { Heading3, Heading4 } from "@/components/Typography";
import { Skeleton } from "@/components/ui/skeleton";
import { graphql, type ResultOf } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { removeNothings } from "@/lib/utils/arrays";

export type Report = NonNullable<ResultOf<typeof reportsListQuery>["reports"]>[number];

export const ReportsList: React.FC = () => {
  return (
    <ErrorBoundary
      FallbackComponent={({ error }) => (
        <ErrorCard
          title="Failed to load reports"
          description={error?.message || "An error occurred while loading reports"}
          className="min-h-[60vh] flex items-center justify-center"
        />
      )}
    >
      <Suspense fallback={<ReportsListSkeleton />}>
        <ReportsListContent />
      </Suspense>
    </ErrorBoundary>
  );
};

const ReportsListContent: React.FC = () => {
  const { activeBusinessUnitIdInt } = useActiveBusinessUnit();
  const cattleSpeciesId = 3;

  if (!activeBusinessUnitIdInt) {
    throw new Error("No business unit selected");
  }

  const { data } = useSuspenseQuery(reportsListQuery, {
    variables: {
      businessUnit: activeBusinessUnitIdInt,
      species: cattleSpeciesId,
      frontend: "SUPPLY_CHAINS",
    },
  });

  const reports = removeNothings(data.reports || []);
  const quicksightReports = reports.filter((report) => report.reportType === "QUICKSIGHT");
  const reportGroups = groupReportsByCategory(quicksightReports);

  if (reportGroups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <div className="relative p-8 rounded-2xl bg-gradient-to-br from-muted/30 to-muted/10 border border-muted/20 backdrop-blur-sm max-w-md">
          <div className="relative mb-6">
            <div className="flex items-end justify-center gap-2 h-16">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="bg-gradient-to-t from-primary/20 to-primary/10 rounded-sm animate-pulse"
                  style={{
                    width: "8px",
                    height: `${20 + (i % 3) * 15}px`,
                    animationDelay: `${i * 0.2}s`,
                    animationDuration: "2s",
                  }}
                />
              ))}
            </div>
            <div className="absolute inset-0">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-1 h-1 bg-primary/30 rounded-full animate-bounce"
                  style={{
                    left: `${30 + i * 20}%`,
                    top: `${20 + i * 10}%`,
                    animationDelay: `${i * 0.5}s`,
                    animationDuration: "3s",
                  }}
                />
              ))}
            </div>
          </div>

          <Heading4 className="mb-2">No reports available</Heading4>
          <p className="text-muted-foreground text-sm">
            Reports will appear here when activated for your business unit.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {reportGroups.map((group) => (
        <div key={group.id}>
          <Heading3 className="mb-4">{group.title}</Heading3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {group.reports.map((report) => (
              <ReportCard key={report.id} report={report} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const ReportCard: React.FC<{ report: Report }> = ({ report }) => {
  const isUpgradeRequired = report.needUpgrade;
  const IconComponent = report.group ? getGroupIcon(report.group.title) : BarChart3;

  if (isUpgradeRequired) {
    return (
      <div className="group flex flex-col gap-3 rounded-lg border p-6 opacity-60">
        <div className="flex items-center gap-3">
          <Lock className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-medium">{report.title}</h3>
        </div>
        <p className="text-sm text-muted-foreground">Upgrade required to access this report</p>
      </div>
    );
  }

  if (!report.slug) {
    return null;
  }

  return (
    <Link
      to={"/reports/$slug"}
      params={{ slug: report.slug }}
      className="group flex flex-col gap-3 rounded-lg border p-6 hover:border-primary/50 hover:bg-accent/50 transition-colors duration-200"
    >
      <div className="flex items-center gap-3">
        <IconComponent className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
        <h3 className="font-medium">{report.title}</h3>
      </div>
    </Link>
  );
};

const ReportsListSkeleton = () => {
  // Skeleton structure that matches typical data: varying group sizes
  const skeletonGroups = [
    { titleWidth: "w-28", reportCount: 8 }, // Performance (longest group)
    { titleWidth: "w-20", reportCount: 2 }, // Financial
    { titleWidth: "w-24", reportCount: 3 }, // Breeding
    { titleWidth: "w-24", reportCount: 4 }, // Inventory
    { titleWidth: "w-32", reportCount: 3 }, // Health & Meds (longest title)
  ];

  return (
    <div className="space-y-8">
      {skeletonGroups.map((group, groupIndex) => (
        <div key={groupIndex}>
          <Skeleton className={`h-7 ${group.titleWidth} mb-4`} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: group.reportCount }).map((_, i) => (
              <div key={i} className="flex flex-col gap-3 rounded-lg border p-6">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-5 w-5" />
                  <Skeleton className="h-6 w-32" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const getGroupIcon = (groupTitle: string) => {
  const iconMap: Record<string, typeof BarChart3> = {
    Performance: Activity,
    Financial: DollarSign,
    Breeding: TrendingUp,
    Inventory: Package,
    "Health & Meds": Heart,
    Supplementary: AlertTriangle,
  };

  return iconMap[groupTitle] || BarChart3;
};

export const reportsListQuery = graphql(`
  query ReportsListQuery($businessUnit: Int, $species: Int, $frontend: ReportFrontendEnum) {
    reports(businessUnit: $businessUnit, species: $species, frontend: $frontend) {
      id
      title
      slug
      position
      needUpgrade
      reportType
      group {
        id
        title
        position
      }
    }
  }
`);
